/** Product page content — landing-style copy for /our-portfolios */

import { pageRoute, serviceRoute } from "./paths.js";
import { productDetailRoute } from "./productDetails.js";
import { serviceNav } from "./content.js";

export const productPage = {
  documentTitle: "Taldel — Our Portfolios",
  hero: {
    titleSegments: [
      { text: "Our Portfolios ", highlight: null },
      { text: "built", highlight: "primary" },
      { text: " to ", highlight: null },
      { text: "ship", highlight: "secondary" },
      { text: " and scale.", highlight: null },
    ],
    subtitle:
      "See how Taldel delivers across web, AI, apps, design, cyber security, and staffing — with the same craft in every project.",
    ctaPrimary: "Talk to us",
    ctaPrimaryHref: pageRoute("our-portfolios", "contact"),
    ctaSecondary: "Explore services",
    ctaSecondaryHref: serviceRoute("web-development"),
    tags: [
      "6 specialties, one team",
      "Live portfolio, real projects",
      "See outcomes, not just logos",
    ],
  },
  showcase: {
    badge: "Product lanes",
    titleSegments: [
      { text: "One scroll. ", highlight: null },
      { text: "Every", highlight: "primary" },
      { text: " service ", highlight: null },
      { text: "surface", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "Stay with a service on the left while capability cards cycle on the right. When a lane finishes, the next service takes focus.",
    services: [
      {
        id: "ai-agentic",
        label: "Agentic AI",
        summary: [
          "Agents that plan, call tools, and finish real work — with guardrails.",
          "Every run is scored and logged, so quality holds as the agent takes on more.",
        ],
        href: serviceRoute("ai-agentic"),
        cards: [
          {
            slug: "cascade-health-intake-agent",
            category: "Orchestration",
            title: "Cascade Health: Intake Agent",
            excerpt: "Multi-step patient intake with tool calling and clear human handoffs.",
            image: "product/case-study-ai-evals.svg",
            imageAlt: "Cascade Health intake agent plan graph and tool-call log",
          },
          {
            slug: "vantage-legal-clause-evals",
            category: "Quality",
            title: "Vantage Legal: Clause Extraction Evals",
            excerpt: "An eval harness that catches regressions before they ship.",
            image: "product/case-study-ai-orchestration.svg",
            imageAlt: "Vantage Legal clause extraction eval dashboard",
          },
        ],
      },
      {
        id: "web-development",
        label: "Web Development",
        summary: [
          "Frontend systems, apps, and marketing surfaces that stay fast and shippable.",
          "Built on modern foundations, with clear ownership from design to deploy.",
        ],
        href: serviceRoute("web-development"),
        cards: [
          {
            slug: "meridian-freight-fleet-ops",
            category: "Case study",
            title: "Meridian Freight: Fleet Ops Dashboard",
            excerpt: "Real-time dispatch visibility for a 200-truck fleet.",
            image: "product/case-study-fleet-dashboard-v4.svg",
            imageAlt: "Meridian Freight fleet operations dashboard",
          },
          {
            slug: "harlow-finch-storefront-rebuild",
            category: "Case study",
            title: "Harlow & Finch: Storefront Rebuild",
            excerpt: "Headless commerce migration that lifted conversion 22%.",
            image: "product/case-study-storefront-rebuild-v4.svg",
            imageAlt: "Harlow and Finch headless storefront rebuild",
          },
        ],
      },
      {
        id: "app-development",
        label: "App Development",
        summary: [
          "Native and cross-platform apps built to feel fast, not just look fast.",
          "Every release ships through the same pipeline that caught the last regression.",
        ],
        href: serviceRoute("app-development"),
        cards: [
          {
            slug: "trailhead-cross-platform-fitness",
            category: "Case study",
            title: "Trailhead: Cross-platform Fitness App",
            excerpt: "An offline-first React Native app syncing across 40K+ daily users.",
            image: "product/case-study-appdev-fitness.svg",
            imageAlt: "Trailhead fitness release dashboard and workout phone screen",
          },
          {
            slug: "cascade-utilities-field-service",
            category: "Case study",
            title: "Cascade Utilities: Field Service App",
            excerpt: "An offline-capable field app for technicians working with no signal.",
            image: "product/case-study-appdev-fieldservice.svg",
            imageAlt: "Cascade Utilities dispatch dashboard and offline work-order phone screen",
          },
        ],
      },
      {
        id: "ui-ux-design",
        label: "UI/UX Design",
        summary: [
          "Journey-led interfaces that reduce friction and survive engineering contact.",
          "Backed by real research, so every screen earns its place before it ships.",
        ],
        href: serviceRoute("ui-ux-design"),
        cards: [
          {
            slug: "ferris-cole-checkout-research",
            category: "Research",
            title: "Ferris & Cole: Checkout Research Sprint",
            excerpt: "Persona cards, emotion-curve maps, and friction finds that cut scope 30%.",
            image: "product/case-study-uiux-research.svg",
            imageAlt: "Ferris and Cole checkout research sprint artifacts",
          },
          {
            slug: "nordly-health-design-system",
            category: "Craft",
            title: "Nordly Health: Design System Craft",
            excerpt: "Components, empty states, and WCAG AA — handoff time down 55%.",
            image: "product/case-study-uiux-craft.svg",
            imageAlt: "Nordly Health design system component craft",
          },
        ],
      },
      {
        id: "cyber-security",
        label: "Cybersecurity",
        summary: [
          "Threat detection and response built to catch what automation misses.",
          "Every incident is triaged, contained, and turned into a hardened defense.",
        ],
        href: serviceRoute("cyber-security"),
        cards: [
          {
            slug: "vertex-defense-managed-detection",
            category: "Case study",
            title: "Vertex Defense: Managed Detection & Response",
            excerpt: "24/7 threat detection with human analysts in the loop",
            image: "product/case-study-cybersecurity-soc.svg",
            imageAlt: "Vertex Defense managed detection and response SOC",
          },
          {
            slug: "ironclad-bank-vulnerability-management",
            category: "Case study",
            title: "Ironclad Bank: Continuous Vulnerability Management",
            excerpt: "Continuous scanning and guided remediation that keeps audits boring",
            image: "product/case-study-cybersecurity-vuln.svg",
            imageAlt: "Ironclad Bank continuous vulnerability management board",
          },
        ],
      },
      {
        id: "staff-augmentation",
        label: "Staff Augmentation",
        summary: [
          "Vetted engineers embedded in your team, shipping inside your existing workflow.",
          "Ramped in days, not months, with the ownership of a full-time hire.",
        ],
        href: serviceRoute("staff-augmentation"),
        cards: [
          {
            slug: "northfield-robotics-embedded",
            category: "Embedded",
            title: "Northfield Robotics: Embedded Platform Team",
            excerpt: "Four senior engineers embedded in an existing sprint cadence in under two weeks.",
            image: "product/case-study-staffaug-embedded.svg",
            imageAlt: "Northfield Robotics embedded platform team",
          },
          {
            slug: "bellcastle-retail-mobile-surge",
            category: "Surge",
            title: "Bellcastle Retail: Mobile Engineering Surge",
            excerpt: "Scaled a mobile team from 2 to 6 engineers to hit a fixed holiday launch date.",
            image: "product/case-study-staffaug-mobile-surge.svg",
            imageAlt: "Bellcastle Retail mobile engineering surge",
          },
        ],
      },
    ],
  },
};

export function getProductPage() {
  return productPage;
}

/** Side-rail links for /our-portfolios — short labels so vertical pills fit */
const PRODUCT_SIDE_LABELS = {
  "ai-agentic": "AI",
  "web-development": "Web",
  "app-development": "Apps",
  "ui-ux-design": "UI/UX",
  "cyber-security": "Cyber",
  "staff-augmentation": "Staff",
};

export function getProductSideNav() {
  const servicesById = Object.fromEntries(
    (productPage.showcase?.services || []).map((service) => [service.id, service])
  );

  return serviceNav
    .map((item) => {
      const service = servicesById[item.slug];
      if (!service) return null;
      return {
        label: PRODUCT_SIDE_LABELS[service.id] || service.label,
        href: productRoute(service.id),
        id: service.id,
      };
    })
    .filter(Boolean);
}

export function productRoute(section = "") {
  return pageRoute("our-portfolios", section);
}

export { productDetailRoute };
